import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from '@/lib/i18n';
import { Button, Dialog, DialogContent, DialogDescription, DialogTitle, Label } from '@/components/ui';
import {
  fundingLaunchSchema,
  type FundingLaunchValues,
} from '../schemas/fundingFormSchema';

export interface FundingLaunchModalProps {
  isOpen: boolean;
  title: string;
  isSubmitting: boolean;
  errorMessage?: string | null;
  hasRewards: boolean;
  /** QA-404 — 목표 금액이 정해졌는지. 미정이면 BE 가 400 FUNDING_INVALID_GOAL 로 거부한다. 기본 true. */
  hasGoal?: boolean;
  onConfirm: (values: FundingLaunchValues) => void;
  onCancel: () => void;
}

export const FundingLaunchModal = ({
  isOpen,
  title,
  isSubmitting,
  errorMessage,
  hasRewards,
  hasGoal = true,
  onConfirm,
  onCancel,
}: FundingLaunchModalProps) => {
  const { t } = useTranslation('funding');
  const form = useForm<FundingLaunchValues>({
    resolver: zodResolver(fundingLaunchSchema),
    defaultValues: { note: '' },
    mode: 'onBlur',
  });

  return (
    <Dialog open={isOpen} onOpenChange={(o) => { if (!o && !isSubmitting) onCancel(); }}>
      <DialogContent data-testid="funding-launch-modal" className="max-w-md">
        <DialogTitle>{t('funding.launch.title')}</DialogTitle>
        <DialogDescription>
          {t('funding.launch.subject', { title })}
        </DialogDescription>

        {!hasGoal ? (
          <p
            role="alert"
            aria-live="polite"
            className="mt-4 rounded-md p-3 text-sm"
            style={{ background: 'var(--status-warn-bg)', color: 'var(--status-warn-fg)' }}
            data-testid="funding-launch-goal-required"
          >
            {t('funding.launch.goalRequired', '목표 금액을 입력해야 런칭할 수 있습니다. [수정]에서 정산 단계의 목표 금액을 넣어 주세요.')}
          </p>
        ) : null}
        {!hasRewards ? (
          <p
            role="alert"
            aria-live="polite"
            className="mt-4 rounded-md p-3 text-sm"
            style={{
              background: 'var(--status-warn-bg)',
              color: 'var(--status-warn-fg)',
            }}
          >
            {t('funding.launch.rewardRequired')}
          </p>
        ) : (
          <p className="mt-4 text-sm text-[color:var(--content-default)]">
            {t('funding.launch.confirm')}
          </p>
        )}

        <form
          onSubmit={form.handleSubmit(onConfirm)}
          className="mt-4 flex flex-col gap-2"
          noValidate
        >
          <Label htmlFor="launch-note">{t('funding.launch.noteLabel')}</Label>
          <textarea
            id="launch-note"
            {...form.register('note')}
            maxLength={500}
            rows={3}
            className="w-full rounded-md border px-3 py-2 text-sm bg-[color:var(--surface-0)] text-[color:var(--content-strong)] focus-visible:outline-none focus-visible:ring-2"
            style={{ borderColor: 'var(--content-muted)' }}
          />

          {errorMessage ? (
            <p
              role="alert"
              aria-live="polite"
              className="mt-1 rounded-md p-2 text-sm"
              style={{
                background: 'var(--status-danger-bg)',
                color: 'var(--status-danger-fg)',
              }}
            >
              {errorMessage}
            </p>
          ) : null}

          <div className="mt-3 flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              size="md"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              {t('funding.actions.cancel')}
            </Button>
            <Button
              type="submit"
              variant="cta"
              size="md"
              disabled={isSubmitting || !hasRewards || !hasGoal}
            >
              {isSubmitting
                ? t('funding.launch.submitting')
                : t('funding.launch.submit')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
